import { apiFetch } from '@/lib/api';
import { reactNavHrefForRouteHref } from '@/lib/react-admin-route-map';

export type AdminNotification = {
  id: number;
  key: string;
  type: string;
  category: string;
  severity: string;
  message: string;
  context: string | null;
  details: string | null;
  entityType: string | null;
  entityId: number | null;
  entityName: string | null;
  siteId: number | null;
  siteName: string | null;
  sopId: number | null;
  sopName: string | null;
  actionUrl: string | null;
  source: string | null;
  metadata?: Record<string, unknown> | null;
  requiredPermission?: string | null;
  actionRequiredPermission?: string | null;
  canAct?: boolean;
  timestamp: number;
  firstSeen: number;
  resolvedAt: number | null;
};

export type AdminNotificationListQuery = {
  includeResolved?: boolean;
  category?: string;
  severity?: string;
  limit?: number;
  offset?: number;
};

export async function fetchAdminNotifications(
  query: AdminNotificationListQuery = {},
): Promise<AdminNotification[]> {
  const params = new URLSearchParams();
  if (query.includeResolved) params.set('includeResolved', 'true');
  if (query.category && query.category !== 'all') params.set('category', query.category);
  if (query.severity && query.severity !== 'all') params.set('severity', query.severity);
  if (query.limit != null) params.set('limit', String(query.limit));
  if (query.offset != null) params.set('offset', String(query.offset));
  const qs = params.toString();
  return apiFetch<AdminNotification[]>(`/api/admin/notifications${qs ? `?${qs}` : ''}`);
}

export async function fetchActiveAdminNotifications(): Promise<AdminNotification[]> {
  return apiFetch<AdminNotification[]>('/api/admin/notifications/active');
}

export async function cleanupAdminNotifications(includeResolved = false): Promise<number> {
  const data = await apiFetch<{ resolved?: number }>('/api/admin/notifications/cleanup', {
    method: 'POST',
    body: JSON.stringify({ includeResolved }),
  });
  return Number(data.resolved ?? 0);
}

export async function fetchAdminNotificationCount(): Promise<number> {
  const data = await apiFetch<{ count: number }>('/api/admin/notifications/count');
  return Number(data.count || 0);
}

export async function resolveAdminNotification(key: string): Promise<void> {
  await apiFetch(`/api/admin/notifications/${encodeURIComponent(key)}/resolve`, {
    method: 'POST',
  });
}

export function reactActionUrl(actionUrl: string | null): string | null {
  if (!actionUrl) return null;
  try {
    const url = new URL(actionUrl, window.location.origin);
    if (url.origin !== window.location.origin) return actionUrl;
    const normalized = url.pathname.replace(/^\/+/, '');
    if (!normalized) return actionUrl;
    const rewritten = reactNavHrefForRouteHref(normalized);
    return `${rewritten}${url.search}${url.hash}`;
  } catch {
    return actionUrl;
  }
}

export function categoryLabel(category: string): string {
  const labels: Record<string, string> = {
    delivery: 'Delivery',
    contacts: 'Contacts',
    sops: 'SOPs',
    readiness: 'Readiness',
    integrations: 'Integrations',
    system: 'System',
    other: 'Other',
  };
  return labels[category] || category.replace(/_/g, ' ');
}
